import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { uploadRecording } from '../services/recordingsService';
import {
  ChevronRight,
  Mic,
  Square,
  Volume2,
  Play,
  Save,
  Sparkles,
  Wind,
  Music,
  Ear,
  Smile,
  Target,
  CheckCircle2,
  AlertCircle,
  Trash2,
  History,
  Send,
} from 'lucide-react';

/* -----------------------------------------------------------
   Audio helpers — uses native browser SpeechSynthesis (fr-FR).
   Coqui TTS is not part of Selora's stack; native synthesis
   gives us free, instant fr-FR voice playback in all browsers.

   Playback rate is stored at module scope so any UI control
   (e.g. the B2 speed toggle) can update it without prop drilling.
----------------------------------------------------------- */
let _playbackRate = 0.95;
const setPlaybackRate = (r) => {
  _playbackRate = r;
};

const speak = (text, rate) => {
  if (typeof window === 'undefined' || !window.speechSynthesis) return;
  try {
    window.speechSynthesis.cancel();
    const utter = new window.SpeechSynthesisUtterance(text);
    utter.lang = 'fr-FR';
    utter.rate = typeof rate === 'number' ? rate : _playbackRate;
    window.speechSynthesis.speak(utter);
  } catch {
    /* silent */
  }
};

/* -----------------------------------------------------------
   Modules definition
----------------------------------------------------------- */
const MODULES = [
  { id: 'prononce', name: 'Prononce & Compare', icon: Mic, hint: "Écoute, répète, compare ta voix au modèle." },
  { id: 'defis', name: 'Défis de Prononciation', icon: Target, hint: "Sons nasaux, voyelles arrondies, le « r » français." },
  { id: 'rythme', name: 'Rythme & Fluidité', icon: Music, hint: "Découvre le rythme naturel du français." },
  { id: 'bouche', name: 'Bouche & Prononciation', icon: Smile, hint: "Position des lèvres, de la langue, du souffle." },
  { id: 'ecoute', name: 'Écoute Active', icon: Ear, hint: "Distingue les sons proches du français." },
];

/* -----------------------------------------------------------
   "Défi A1 — Les mots qui chantent" — featured 6-word exercise
----------------------------------------------------------- */
const FEATURED_WORDS = [
  {
    word: 'incroyable',
    ipa: '/ɛ̃.kʁwa.jabl/',
    syllables: ['in', 'cro', 'ya', 'ble'],
    focus: 'Son nasal « in »',
    hint: 'Le « in » est nasal : laisse l\'air passer aussi par le nez.',
  },
  {
    word: "aujourd'hui",
    ipa: '/o.ʒuʁ.dɥi/',
    syllables: ['au', "jour", "d'hui"],
    focus: 'Le « r » guttural',
    hint: 'Le « r » français vient de la gorge, pas du bout de la langue.',
  },
  {
    word: 'papillon',
    ipa: '/pa.pi.jɔ̃/',
    syllables: ['pa', 'pi', 'llon'],
    focus: 'Son nasal « on »',
    hint: 'Le « on » dans papillon est nasal et arrondi : lèvres en O.',
  },
  {
    word: 'chocolat',
    ipa: '/ʃɔ.kɔ.la/',
    syllables: ['cho', 'co', 'lat'],
    focus: 'Voyelles ouvertes',
    hint: 'Ouvre bien la bouche sur les deux « o ».',
  },
  {
    word: 'musique',
    ipa: '/my.zik/',
    syllables: ['mu', 'si', 'que'],
    focus: 'Voyelle « u » arrondie',
    hint: 'Le « u » demande des lèvres bien arrondies, langue avancée.',
  },
  {
    word: 'voyage',
    ipa: '/vwa.jaʒ/',
    syllables: ['vo', 'ya', 'ge'],
    focus: 'Liaison « v + oya »',
    hint: 'Enchaîne « vo-ya » en une seule respiration.',
  },
];

/* Real-life A1 sentences — focus on rhythm, liaisons, signature sounds. */
const FEATURED_SENTENCES = [
  {
    id: 's1',
    sentence: 'Bonjour, comment vas-tu ?',
    focus: 'Salutation · son nasal',
    hint: "« Bon-jour » porte un son nasal sur « on ». Marque une petite pause après la virgule.",
    context: 'Saluer un ami',
  },
  {
    id: 's2',
    sentence: "Je m'appelle Sophie.",
    focus: 'Liaison « m\'a »',
    hint: "Enchaîne « Je m'a-ppelle » sans pause. Le « e » final de Sophie est silencieux.",
    context: 'Se présenter',
  },
  {
    id: 's3',
    sentence: "J'habite à Paris.",
    focus: '« h » muet · liaison',
    hint: "Le « h » de habite est muet : on dit « J'a-bite ». Petite liaison sur « habite-à ».",
    context: 'Dire où tu vis',
  },
  {
    id: 's4',
    sentence: "Il fait beau aujourd'hui.",
    focus: 'Rythme · 3 groupes',
    hint: "Lis comme trois petits blocs : « Il fait beau / aujourd'hui ». Pas de pause au milieu d'un bloc.",
    context: 'Parler du temps',
  },
  {
    id: 's5',
    sentence: 'Tu veux un café ?',
    focus: 'Voyelle « u » · question simple',
    hint: "« Tu » et « veux » demandent des lèvres bien arrondies. Monte la voix à la fin pour la question.",
    context: 'Proposer quelque chose',
  },
  {
    id: 's6',
    sentence: "Je voudrais un croissant, s'il vous plaît.",
    focus: 'Politesse · liaison « vous_plaît »',
    hint: "Enchaîne « s'il-vous-plaît » comme un seul groupe musical. Ralentis un peu, c'est la phrase polie classique.",
    context: 'Commander au café',
  },
  {
    id: 's7',
    sentence: 'On y va ?',
    focus: 'Phrase courte · nasal « on »',
    hint: "Trois petits mots, un seul souffle. Monte légèrement à la fin pour faire la question.",
    context: 'Partir avec quelqu\'un',
  },
  {
    id: 's8',
    sentence: 'À demain !',
    focus: 'Nasal « ain » · enthousiasme',
    hint: "Le « ain » de « demain » est nasal. Donne un peu d'énergie à la fin pour montrer ton sourire.",
    context: 'Dire au revoir',
  },
  {
    id: 's9',
    sentence: 'Quelle belle journée !',
    focus: 'Voyelles « elle » répétées',
    hint: "Les deux « elle » sonnent pareil. Garde un rythme régulier et termine sur une note joyeuse.",
    context: "S'exclamer",
  },
  {
    id: 's10',
    sentence: 'Merci beaucoup, à bientôt.',
    focus: 'Liaison · nasal « bien »',
    hint: "Enchaîne « Mer-ci-beau-coup » d'un seul souffle. « bien » est nasal, garde la bouche détendue.",
    context: 'Remercier en partant',
  },
];

/* -----------------------------------------------------------
   Défis : sons difficiles
----------------------------------------------------------- */
const DEFI_SOUNDS = [
  { sound: 'R français', words: ['rouge', 'parler', 'racine', 'paris'], hint: 'Le « r » vibre dans la gorge, comme un raclement doux.' },
  { sound: 'Nasales (an, on, in)', words: ['enfant', 'maison', 'pain', 'monde'], hint: 'L\'air sort aussi par le nez. Ne ferme pas la bouche.' },
  { sound: 'Voyelle « u »', words: ['tu', 'rue', 'minute', 'voiture'], hint: 'Lèvres arrondies en avant, comme pour souffler.' },
  { sound: 'Voyelle « eu »', words: ['deux', 'fleur', 'jeune', 'peur'], hint: 'Bouche entrouverte, lèvres légèrement avancées.' },
];

/* -----------------------------------------------------------
   Rythme & Fluidité
----------------------------------------------------------- */
const RHYTHM_SENTENCES = [
  { sentence: "Aujourd'hui / je visite / un musée français.", chunks: ["Aujourd'hui", 'je visite', 'un musée français'] },
  { sentence: 'Le matin / je bois / un café chaud.', chunks: ['Le matin', 'je bois', 'un café chaud'] },
  { sentence: 'Demain / nous partons / en voyage.', chunks: ['Demain', 'nous partons', 'en voyage'] },
];

/* -----------------------------------------------------------
   Bouche : articulation cards
----------------------------------------------------------- */
const MOUTH_CARDS = [
  {
    sound: 'u',
    title: 'La voyelle « u »',
    lips: 'Lèvres très arrondies et avancées (comme un petit O)',
    tongue: 'Langue avancée, pointe contre les dents du bas',
    air: 'Souffle continu, sans tension',
    examples: ['tu', 'rue', 'voiture'],
    mirror: 'Observe tes lèvres dans un miroir : elles doivent former un petit cercle.',
  },
  {
    sound: 'r',
    title: 'Le « r » français',
    lips: 'Lèvres relâchées, légèrement entrouvertes',
    tongue: 'Pointe de la langue derrière les dents du bas',
    air: 'Vibration douce au fond de la gorge',
    examples: ['rouge', 'paris', 'merci'],
    mirror: 'Pose ta main sur ta gorge : tu dois sentir une légère vibration.',
  },
  {
    sound: 'on',
    title: 'Le son nasal « on »',
    lips: 'Lèvres arrondies, comme pour un grand O',
    tongue: 'Langue détendue au milieu de la bouche',
    air: 'L\'air sort en même temps par la bouche et le nez',
    examples: ['bonjour', 'maison', 'papillon'],
    mirror: 'Pince légèrement ton nez : le son doit changer si la nasalité est correcte.',
  },
];

/* -----------------------------------------------------------
   Écoute Active : discrimination pairs
----------------------------------------------------------- */
/* -----------------------------------------------------------
   Écoute Active : discrimination pairs (A1 baseline)
----------------------------------------------------------- */
const ECOUTE_PAIRS = [
  { target: 'rue', distractor: 'roue', focus: 'Différence u / ou', explanation: '« u » = lèvres arrondies serrées · « ou » = lèvres relâchées.' },
  { target: 'dessus', distractor: 'dessous', focus: 'Différence u / ou', explanation: 'Le « u » est plus aigu, le « ou » est plus grave.' },
  { target: 'pain', distractor: 'paix', focus: 'Nasal vs voyelle pure', explanation: '« pain » est nasal (in), « paix » est une voyelle pure (è).' },
  { target: 'an', distractor: 'en', focus: 'Sons nasaux quasi identiques', explanation: 'En français moderne, « an » et « en » se prononcent souvent pareil.' },
];

/* ===========================================================
   A2 DATASETS — progressive challenge over A1
=========================================================== */
const A2_FEATURED_WORDS = [
  {
    word: 'déjeuner',
    ipa: '/de.ʒø.ne/',
    syllables: ['dé', 'jeu', 'ner'],
    focus: 'Voyelle « eu » fermée',
    hint: "Le « eu » de jeuner est fermé : lèvres arrondies en avant, langue détendue.",
  },
  {
    word: 'appartement',
    ipa: '/a.paʁ.tə.mɑ̃/',
    syllables: ['a', 'ppar', 'te', 'ment'],
    focus: 'Nasal final « ent »',
    hint: 'La fin « -ment » est nasale. Ne ferme pas la bouche au dernier son.',
  },
  {
    word: 'téléphone',
    ipa: '/te.le.fɔn/',
    syllables: ['té', 'lé', 'phone'],
    focus: '« ph » = « f »',
    hint: "Le « ph » se prononce comme un simple « f ». N'ajoute pas d'air supplémentaire.",
  },
  {
    word: 'voisin',
    ipa: '/vwa.zɛ̃/',
    syllables: ['voi', 'sin'],
    focus: 'Nasal « in »',
    hint: "Le « in » de voisin est nasal et un peu ouvert. Laisse passer l'air aussi par le nez.",
  },
  {
    word: 'cuisine',
    ipa: '/kɥi.zin/',
    syllables: ['cui', 'si', 'ne'],
    focus: 'Transition « ui »',
    hint: "Le « ui » est une transition fluide u → i. Un seul son, pas deux séparés.",
  },
];

const A2_FEATURED_SENTENCES = [
  // Nasals in context (Activity 3)
  { id: 'a2-s1', sentence: 'Mon voisin est très gentil.', focus: 'Nasaux on / in', hint: "Trois sons nasaux : « on », « in », « en ». Laisse l'air passer aussi par le nez.", context: 'Décrire quelqu\'un' },
  { id: 'a2-s2', sentence: 'Nous mangeons dans un grand restaurant.', focus: 'Nasaux en chaîne', hint: "Quatre nasaux dans la phrase ! Garde un rythme régulier sans accélérer.", context: 'Parler de tes sorties' },
  { id: 'a2-s3', sentence: 'Quentin visite un jardin magnifique.', focus: 'Nasaux « in »', hint: "Les trois « in » sonnent presque pareil. Garde la bouche légèrement entrouverte.", context: 'Raconter une visite' },
  // Natural intonation (Activity 7)
  { id: 'a2-s4', sentence: 'Tu viens avec nous ce soir ?', focus: 'Intonation ↗', hint: "Monte clairement la voix sur le dernier mot pour signaler la question.", context: 'Inviter un ami' },
  { id: 'a2-s5', sentence: "J'adore écouter de la musique française.", focus: 'Mélodie · voyelle « u »', hint: "Mets de l'émotion sur « adore ». Le « u » de musique = lèvres arrondies.", context: 'Partager une passion' },
  { id: 'a2-s6', sentence: 'Pourquoi est-ce que tu ris ?', focus: 'Intonation ↗ · curiosité', hint: "Donne un ton intrigué : la voix monte légèrement à la fin.", context: 'Demander une explication' },
  // Pronunciation Express — timed (Activity 9)
  { id: 'a2-s7', sentence: 'Je voudrais réserver une chambre.', focus: 'Défi rapide ⏱', hint: "Dis cette phrase d'un seul souffle sans pause. Articule bien le « r » de réserver.", context: 'À l\'hôtel' },
  { id: 'a2-s8', sentence: "Nous visitons un musée aujourd'hui.", focus: 'Défi rapide ⏱', hint: "Quatre groupes rythmiques compacts. Ne ralentis pas au milieu.", context: 'Programme du jour' },
  { id: 'a2-s9', sentence: 'Ce restaurant est vraiment excellent.', focus: 'Défi rapide ⏱', hint: "Marque l'enthousiasme sur « vraiment ». Le « ex- » de excellent = « eg ».", context: 'Donner ton avis' },
  // Mini Dialogue Immersif (Activity 10)
  { id: 'a2-s10', sentence: '— Bonjour, vous désirez ?', focus: 'Dialogue · politesse', hint: "Ton accueillant et calme. Petite pause après « Bonjour ».", context: 'Mini dialogue · le commerçant' },
  { id: 'a2-s11', sentence: "— Je cherche une boulangerie près d'ici.", focus: 'Dialogue · demande', hint: "Sois clair sur « boulangerie ». Le « r » de cherche est doux.", context: 'Mini dialogue · le client' },
  { id: 'a2-s12', sentence: '— Oui, continuez tout droit puis tournez à gauche.', focus: 'Dialogue · indication', hint: "Marque les deux étapes : « tout droit » puis « à gauche ». Une vraie petite carte parlée.", context: 'Mini dialogue · la réponse' },
];

const A2_DEFI_SOUNDS = [
  { sound: 'Voyelles « é » / « è »', words: ['été', 'mère', 'parler', 'frère'], hint: "« é » = bouche presque fermée, sourire léger. « è » = bouche plus ouverte." },
  { sound: 'Voyelles « eu » / « œ »', words: ['peu', 'peur', 'jeune', 'fleur'], hint: "« eu » fermé (peu) = lèvres avancées. « œ » ouvert (peur) = bouche plus relâchée." },
  { sound: 'Voyelles « ou » / « u »', words: ['roue', 'rue', 'tout', 'tu'], hint: "« ou » = lèvres très avancées, son grave. « u » = lèvres serrées, son aigu." },
  { sound: 'Le « R » en contexte', words: ['restaurant', 'regarder', 'Paris', 'incroyable'], hint: "Vibration douce dans la gorge. Plus le mot est long, plus tu peux relâcher le « r »." },
];

const A2_RHYTHM_SENTENCES = [
  // Activity 4 — Le rythme français
  { sentence: 'Ce matin / je prends le métro.', chunks: ['Ce matin', 'je prends le métro'] },
  { sentence: 'Après le travail / nous allons au cinéma.', chunks: ['Après le travail', 'nous allons au cinéma'] },
  { sentence: 'Pendant les vacances / ils visitent Marseille.', chunks: ['Pendant les vacances', 'ils visitent Marseille'] },
  // Activity 5 — Les liaisons faciles
  { sentence: 'Vous avez compris ?', chunks: ['Vous_avez', 'compris ?'], focus: 'Liaison vous-avez' },
  { sentence: 'Ils arrivent demain.', chunks: ['Ils_arrivent', 'demain'], focus: 'Liaison ils-arrivent' },
  { sentence: "C'est un petit enfant.", chunks: ["C'est un", 'petit_enfant'], focus: 'Liaison petit-enfant' },
  { sentence: "C'est très intéressant.", chunks: ["C'est", 'très_intéressant'], focus: 'Liaison très-intéressant' },
];

const A2_MOUTH_CARDS = [
  {
    sound: 'eu',
    title: 'La voyelle « eu »',
    lips: 'Lèvres arrondies et légèrement avancées (entre o et u)',
    tongue: 'Langue au centre de la bouche, détendue',
    air: 'Souffle régulier, son chantant',
    examples: ['deux', 'peu', 'jeune'],
    mirror: 'Observe : tes lèvres forment un petit ovale, pas un cercle complet comme pour « u ».',
  },
  {
    sound: 'é / è',
    title: 'Les voyelles « é » et « è »',
    lips: 'É : presque fermées, sourire léger · È : un peu plus ouvertes',
    tongue: 'Avancée dans les deux cas, vers les dents',
    air: 'Plus tendu pour « é », plus relâché pour « è »',
    examples: ['été', 'mère', 'parler'],
    mirror: 'Devant un miroir : tes lèvres bougent légèrement entre « é » et « è ». Sens la différence.',
  },
  {
    sound: 'R français',
    title: 'Le « R » français (en profondeur)',
    lips: 'Lèvres relâchées, légèrement entrouvertes',
    tongue: 'Pointe contre les dents du bas, dos de la langue surélevé',
    air: 'Vibration douce, presque comme un raclement très léger',
    examples: ['restaurant', 'regarder', 'incroyable'],
    mirror: 'Pose ta main sur ta gorge en disant « r-r-r » : tu dois sentir une vibration nette.',
  },
];

const A2_ECOUTE_PAIRS = [
  { target: 'brun', distractor: 'brin', focus: 'Nasaux « un » / « in »', explanation: "« brun » = nasal arrondi (un). « brin » = nasal plus aigu (in). Différence subtile mais nette." },
  { target: 'peur', distractor: 'port', focus: 'Voyelles « eu » / « o »', explanation: "« peur » = lèvres avancées, son chantant. « port » = bouche plus ouverte, son grave." },
  { target: 'dessus', distractor: 'dessous', focus: 'Voyelles « u » / « ou »', explanation: "« dessus » = au-dessus (lèvres serrées). « dessous » = en-dessous (lèvres avancées)." },
  { target: 'tu', distractor: 'tout', focus: 'Voyelles « u » / « ou »', explanation: "« tu » = pronom (lèvres serrées). « tout » = totalité (lèvres avancées)." },
  { target: 'été', distractor: 'était', focus: 'Voyelles « é » / « è »', explanation: "« été » = saison (é fermé). « était » = imparfait (è ouvert)." },
  { target: 'rue', distractor: 'roue', focus: 'Voyelles « u » / « ou »', explanation: "« rue » = la rue (u aigu). « roue » = la roue de la voiture (ou grave)." },
];

/* ===========================================================
   B1 DATASETS — fluency, longer words, sentence melody
=========================================================== */
const B1_FEATURED_WORDS = [
  { word: 'extraordinaire', ipa: '/ɛk.stʁa.ɔʁ.di.nɛʁ/', syllables: ['ex', 'tra', 'or', 'di', 'naire'], focus: 'Voyelles enchaînées', hint: "Enchaîne « ex-tra-or » sans pause. Le « r » reste doux dans chaque syllabe." },
  { word: 'malheureusement', ipa: '/ma.lœ.ʁøz.mɑ̃/', syllables: ['mal', 'heu', 'reu', 'se', 'ment'], focus: 'Deux « eu » + nasal', hint: "Deux sons « eu » de qualité différente. Termine sur le nasal « ment »." },
  { word: 'universitaire', ipa: '/y.ni.vɛʁ.si.tɛʁ/', syllables: ['u', 'ni', 'ver', 'si', 'taire'], focus: 'Deux « r » à doser', hint: "Le « u » initial : lèvres bien serrées. Les deux « r » restent légers." },
  { word: 'environnement', ipa: '/ɑ̃.vi.ʁɔn.mɑ̃/', syllables: ['en', 'vi', 'ron', 'ne', 'ment'], focus: 'Trois nasaux !', hint: "« en », « on », « ment » : trois nasaux dans un seul mot. Garde la bouche détendue." },
  { word: 'incontournable', ipa: '/ɛ̃.kɔ̃.tuʁ.nabl/', syllables: ['in', 'con', 'tour', 'nable'], focus: 'Deux nasaux + « R »', hint: "« in » et « con » sonnent légèrement différents. Garde le rythme régulier." },
  { word: 'impressionnant', ipa: '/ɛ̃.pʁɛ.sjɔ.nɑ̃/', syllables: ['im', 'pre', 'ssion', 'nant'], focus: 'Nasaux en cascade', hint: "Trois nasaux dans un mot ! Ne ralentis pas, garde un débit fluide." },
  { word: 'immédiatement', ipa: '/i.me.dja.tə.mɑ̃/', syllables: ['i', 'mmé', 'dia', 'te', 'ment'], focus: '« dia » fluide', hint: "Le « dia » est une semi-voyelle rapide. Ne le coupe pas en « di-a »." },
  { word: 'passionnément', ipa: '/pa.sjɔ.ne.mɑ̃/', syllables: ['pa', 'ssio', 'nné', 'ment'], focus: '« sio » et nasal', hint: "« ssio » se prononce en une seule syllabe rapide. Termine sur « ment » nasal." },
  { word: 'silencieusement', ipa: '/si.lɑ̃.sjøz.mɑ̃/', syllables: ['si', 'len', 'cieu', 'se', 'ment'], focus: 'Nasal + « eu » fermé', hint: "« len » est nasal, « cieu » utilise un « eu » fermé. Ralentis légèrement sur « cieu »." },
  { word: 'régulièrement', ipa: '/ʁe.ɡy.ljɛʁ.mɑ̃/', syllables: ['ré', 'gu', 'liè', 're', 'ment'], focus: '« u » serré + nasal', hint: "Le « u » de gu demande des lèvres bien arrondies. Termine sur le nasal final." },
];

const B1_FEATURED_SENTENCES = [
  // Activity 2 — Enchaînements difficiles
  { id: 'b1-s1', sentence: "Nous avons énormément appris aujourd'hui.", focus: 'Enchaînement vocalique', hint: "Enchaîne « nous_avons_énormément » sans coupure. Une seule respiration jusqu'à « aujourd'hui ».", context: 'Réflexion sur une journée' },
  { id: 'b1-s2', sentence: 'Ils ont organisé un événement exceptionnel.', focus: 'Liaisons en chaîne', hint: "Trois liaisons : ils_ont, organisé_un, événement_exceptionnel. Un seul souffle.", context: 'Raconter un succès' },
  { id: 'b1-s3', sentence: 'Vous êtes arrivés exactement au bon moment.', focus: 'Liaisons + nasal', hint: "« vous_êtes_arrivés » : enchaîne sans pause. Articule clairement « exactement ».", context: 'Accueillir quelqu\'un' },
  { id: 'b1-s4', sentence: 'Ces étudiants ont énormément progressé.', focus: 'Liaison étudiants_ont', hint: "Liaison forte entre « étudiants_ont ». Mets de l\'énergie sur « énormément ».", context: 'Parler d\'évolution' },
  { id: 'b1-s5', sentence: 'On a immédiatement accepté leur proposition.', focus: 'Adverbe rapide', hint: "« immédiatement » d'un trait, sans hacher les syllabes. La fluidité est la clé ici.", context: 'Décision rapide' },
  // Activity 3 — Nasaux avancés
  { id: 'b1-s6', sentence: 'Ce peintre parisien est vraiment impressionnant.', focus: 'Nasaux en / ien / an', hint: "Trois sons nasaux différents : « pein- », « pari-sien », « impressi-onnant ». Garde la résonance.", context: 'Parler d\'un artiste' },
  { id: 'b1-s7', sentence: 'Quentin comprend rapidement les conversations françaises.', focus: 'Nasaux en chaîne', hint: "« Quen- », « com-prend », « conver-sations », « fran-çaises ». Quatre nasaux : pas d\'exagération.", context: 'Décrire les compétences' },
  { id: 'b1-s8', sentence: 'Nous visitons souvent les anciens monuments.', focus: 'Nasaux « on » répétés', hint: "« visi-tons », « souvent », « anciens », « monu-ments » : la même résonance fluide partout.", context: 'Hobby culturel' },
  { id: 'b1-s9', sentence: 'Cette ambiance me semble incroyablement apaisante.', focus: 'Émotion + nasaux', hint: "« ambiance » et « incroyablement » contiennent des nasaux. Garde un ton calme et posé.", context: 'Décrire un lieu' },
  { id: 'b1-s10', sentence: 'Le lendemain matin, tout semblait silencieux.', focus: 'Atmosphère narrative', hint: "Lis comme un narrateur de roman : ralentis sur « silencieux », baisse la voix.", context: 'Narration au passé' },
  // Activity 5 — Intonation & émotions
  { id: 'b1-s11', sentence: "C'est absolument incroyable !", focus: 'Émotion · surprise ↗', hint: "Monte clairement sur « incroyable ». Donne de la force à « absolument ».", context: 'S\'émerveiller' },
  { id: 'b1-s12', sentence: 'Tu es vraiment sérieux ?', focus: 'Émotion · doute ↗', hint: "Intonation montante de doute. Insiste sur « vraiment » avec hésitation.", context: 'Demander confirmation' },
  { id: 'b1-s13', sentence: "Je ne m'attendais pas du tout à ça…", focus: 'Émotion · déception ↘', hint: "Voix qui descend lentement, traîne sur « ça ». Comme un soupir contenu.", context: 'Réagir à une surprise' },
  { id: 'b1-s14', sentence: 'Franchement, je trouve cette idée brillante.', focus: 'Émotion · admiration', hint: "Pause après « franchement », puis monte légèrement sur « brillante ».", context: 'Donner un avis sincère' },
  { id: 'b1-s15', sentence: 'Euh… je ne suis pas certain de comprendre.', focus: 'Émotion · hésitation', hint: "Le « euh » authentique, suivi d'une voix douce et incertaine.", context: 'Avouer une incompréhension' },
  // Activity 6 — Phrases longues
  { id: 'b1-s16', sentence: 'Même si la situation semblait compliquée au début, nous avons finalement trouvé une solution efficace.', focus: 'Phrase longue · 2 souffles', hint: "Marque une pause nette après « début ». Deux groupes rythmiques équilibrés.", context: 'Récit de résolution' },
  { id: 'b1-s17', sentence: "Après avoir terminé son mémoire universitaire, elle a décidé de voyager à travers plusieurs pays européens.", focus: 'Phrase longue · narratif', hint: "Respire après « universitaire ». Le deuxième groupe a son propre élan.", context: 'Parcours après les études' },
  { id: 'b1-s18', sentence: "Comme il pleuvait énormément depuis le matin, les organisateurs ont annulé l'événement en extérieur.", focus: 'Phrase longue · cause/effet', hint: "Pause nette après « matin ». Pose la cause, puis la conséquence.", context: 'Annonce officielle' },
  { id: 'b1-s19', sentence: "Bien qu'il soit extrêmement talentueux, il reste toujours très modeste avec ses collègues.", focus: 'Phrase longue · contraste', hint: "Marque l'opposition par une pause forte après « talentueux ».", context: 'Décrire un caractère' },
  { id: 'b1-s20', sentence: 'Pendant que nous visitions le musée, un guide nous a raconté une histoire fascinante sur cette époque.', focus: 'Phrase longue · récit', hint: "Premier groupe : action en cours. Deuxième groupe : événement principal.", context: 'Anecdote culturelle' },
  // Activity 7 — Défi rapide
  { id: 'b1-s21', sentence: "J'aimerais réserver une table pour quatre personnes ce soir.", focus: 'Défi rapide ⏱', hint: "Phrase de café/restaurant. Dis-la sans hésiter, ton naturel et poli.", context: 'Au restaurant' },
  { id: 'b1-s22', sentence: 'Cette exposition artistique attire énormément de visiteurs.', focus: 'Défi rapide ⏱', hint: "Articulation soignée sur « exposition artistique ». Pas d'arrêt au milieu.", context: 'Parler d\'art' },
  { id: 'b1-s23', sentence: 'Nous avons malheureusement oublié les documents importants.', focus: 'Défi rapide ⏱', hint: "Ton un peu désolé sur « malheureusement ». Garde la phrase fluide.", context: 'Au bureau' },
  { id: 'b1-s24', sentence: 'Le réalisateur a présenté son nouveau film au festival.', focus: 'Défi rapide ⏱', hint: "Trois liaisons subtiles. Ton informatif comme un présentateur.", context: 'Actualité cinéma' },
  { id: 'b1-s25', sentence: "Beaucoup d'étudiants participent activement à cette conférence.", focus: 'Défi rapide ⏱', hint: "« d\'étudiants » se dit d'un trait. Articule « activement » clairement.", context: 'Vie universitaire' },
  // Activity 9 — Mini débat (open prompts)
  { id: 'b1-s26', sentence: 'Préfères-tu vivre à la campagne ou en ville ?', focus: 'Débat · prends position', hint: "Réponds librement pendant 30 à 60 secondes. Donne 2 raisons concrètes.", context: 'Mini débat oral' },
  { id: 'b1-s27', sentence: 'Les réseaux sociaux sont-ils utiles ?', focus: 'Débat · argumente', hint: "Adopte une vraie position. Nuance avec « cela dit » ou « cependant ».", context: 'Mini débat oral' },
  { id: 'b1-s28', sentence: "Faut-il voyager pour apprendre une langue ?", focus: 'Débat · ton personnel', hint: "Partage ton expérience. 30 à 60 secondes de parole spontanée.", context: 'Mini débat oral' },
  // Activity 10 — Immersion conversationnelle (4-line dialogue)
  { id: 'b1-s29', sentence: "— Alors, qu'est-ce que tu as pensé de ton séjour à Montréal ?", focus: 'Dialogue · question ouverte', hint: "Ton chaleureux et curieux. Pause naturelle après « Alors ».", context: 'Dialogue · l\'ami curieux' },
  { id: 'b1-s30', sentence: "— Honnêtement, j'ai adoré l'ambiance et les gens étaient extrêmement accueillants.", focus: 'Dialogue · enthousiasme', hint: "Sincérité dans la voix. Marque « adoré » et « accueillants ».", context: 'Dialogue · le voyageur enthousiaste' },
  { id: 'b1-s31', sentence: '— Ah oui ? Qu\'est-ce qui t\'a le plus marqué ?', focus: 'Dialogue · relance', hint: "« Ah oui ? » : surprise légère qui invite à continuer. Voix montante.", context: 'Dialogue · la relance' },
  { id: 'b1-s32', sentence: "— Probablement l'architecture et la diversité culturelle.", focus: 'Dialogue · réflexion', hint: "Ton pensif sur « probablement ». Donne du poids à « diversité culturelle ».", context: 'Dialogue · la réponse réfléchie' },
  // 5 additional advanced sentences for richness
  { id: 'b1-s33', sentence: "Heureusement, personne ne s'est rendu compte de notre erreur pendant la présentation.", focus: 'Phrase complexe · soulagement', hint: "Pause après « heureusement ». Ton un peu confidentiel.", context: 'Anecdote professionnelle' },
  { id: 'b1-s34', sentence: "Cette expérience professionnelle m'a permis de gagner énormément de confiance en moi.", focus: 'Phrase complexe · bilan', hint: "Insiste sur « énormément » et « confiance ». Ton mature et posé.", context: 'Parler de soi' },
  { id: 'b1-s35', sentence: "À cause des conditions météorologiques, notre vol a été retardé pendant plusieurs heures.", focus: 'Phrase complexe · cause', hint: "Ton calme et factuel. Pause après « météorologiques ».", context: 'Récit de voyage' },
  { id: 'b1-s36', sentence: 'La technologie transforme progressivement notre manière de communiquer au quotidien.', focus: 'Phrase complexe · réflexion', hint: "Ralentis légèrement sur « progressivement ». Ton presque journalistique.", context: 'Opinion de société' },
  { id: 'b1-s37', sentence: "Après avoir réfléchi longuement, elle a finalement accepté leur proposition.", focus: 'Phrase complexe · narration', hint: "Marque les deux temps : la réflexion, puis la décision. Pause naturelle.", context: 'Récit court' },
];

const B1_DEFI_SOUNDS = [
  { sound: 'Semi-voyelles [j] [w] [ɥ]', words: ['fille', 'voyage', 'nuit', 'huit'], hint: "Sons rapides qui glissent : ne les coupe pas en deux syllabes." },
  { sound: 'Le « gn »', words: ['gagner', 'montagne', 'magnifique', 'signature'], hint: "Une seule consonne nasale-palatale. Pas deux sons séparés." },
  { sound: 'Groupes consonantiques', words: ['stratégie', 'exclusivement', 'structure', 'expression'], hint: "Articule chaque consonne, mais sans rompre le débit." },
  { sound: 'Le « R » enchaîné', words: ['trois', 'croire', 'prendre', 'propre'], hint: "Le « r » s'enchaîne avec la consonne précédente. Souffle continu." },
];

const B1_RHYTHM_SENTENCES = [
  { sentence: "Après plusieurs années d'études / elle a enfin obtenu son diplôme.", chunks: ["Après plusieurs années d'études", 'elle a enfin obtenu son diplôme'] },
  { sentence: "Pendant les vacances d'été / nous avons découvert un village magnifique.", chunks: ["Pendant les vacances d'été", 'nous avons découvert un village magnifique'] },
  { sentence: 'Dès que la conférence commencera / veuillez éteindre vos téléphones.', chunks: ['Dès que la conférence commencera', 'veuillez éteindre vos téléphones'] },
  { sentence: 'Malgré les difficultés rencontrées / ils ont continué leur projet.', chunks: ['Malgré les difficultés rencontrées', 'ils ont continué leur projet'] },
  { sentence: "En regardant ce documentaire / j'ai appris énormément de choses.", chunks: ['En regardant ce documentaire', "j'ai appris énormément de choses"] },
];

const B1_MOUTH_CARDS = [
  {
    sound: 'Semi-voyelles',
    title: 'Les semi-voyelles [j], [w], [ɥ]',
    lips: '[j] yeux légèrement souriants · [w] lèvres avancées · [ɥ] lèvres très serrées',
    tongue: 'Avancée et active, transition rapide',
    air: 'Très bref — la semi-voyelle « glisse » vers la voyelle suivante',
    examples: ['yeux', 'oui', 'lui'],
    mirror: 'Observe : tes lèvres bougent VITE entre la semi-voyelle et la voyelle qui suit.',
  },
  {
    sound: 'Enchaînement',
    title: "L'enchaînement consonantique",
    lips: 'Lèvres relâchées entre les mots',
    tongue: 'Pas d\'arrêt entre la consonne finale et la voyelle suivante',
    air: 'Souffle continu — pas de petite pause cachée',
    examples: ['une_amie', 'avec_eux', 'sept_heures'],
    mirror: 'Enregistre-toi et écoute : entends-tu une coupure ? Si oui, recommence en glissant.',
  },
  {
    sound: 'Souffle long',
    title: 'Le souffle long et la phrase fluide',
    lips: 'Détendues, posture vocale ouverte',
    tongue: 'Reste mobile et active sans tension',
    air: 'Respire profondément avant chaque groupe rythmique',
    examples: ['phrases longues', 'récits', 'dialogues'],
    mirror: "Place ta main sur ton ventre : il doit se gonfler à l'inspiration, pas tes épaules.",
  },
];

const B1_ECOUTE_PAIRS = [
  { target: 'intention', distractor: 'attention', focus: 'Nasaux « in » / « at »', explanation: "« intention » commence par un nasal (in-). « attention » commence par une voyelle pure (a-)." },
  { target: 'dessus', distractor: 'dessous', focus: 'Voyelles « u » / « ou »', explanation: "« dessus » = au-dessus (u aigu). « dessous » = en-dessous (ou grave)." },
  { target: 'désert', distractor: 'dessert', focus: '« s » sonore vs « ss » sourd', explanation: "« désert » = le « s » se prononce comme un « z ». « dessert » = double « s » sourd." },
  { target: 'événement', distractor: 'avènement', focus: 'Voyelles initiales é / a', explanation: "« événement » = un fait. « avènement » = arrivée solennelle de quelque chose ou quelqu'un." },
  { target: 'tâche', distractor: 'tache', focus: 'Voyelle « â » longue', explanation: "« tâche » = un travail à faire (â plus long, plus profond). « tache » = une marque de salissure." },
];

/* ===========================================================
   B2 DATASETS — fluency at native speed
=========================================================== */
const B2_FEATURED_WORDS = [
  { word: 'transparence', ipa: '/tʁɑ̃s.paʁɑ̃s/', syllables: ['trans', 'pa', 'rence'], focus: 'Nasaux répétés', hint: "Deux nasaux « an » presque identiques. Garde un débit régulier sans accentuer." },
  { word: 'transporteur', ipa: '/tʁɑ̃s.pɔʁ.tœʁ/', syllables: ['trans', 'por', 'teur'], focus: 'Nasal + 2 « R »', hint: "Trois consonnes complexes à enchaîner. Les « r » restent doux." },
  { word: 'anticonstitutionnellement', ipa: '/ɑ̃.ti.kɔ̃s.ti.ty.sjɔ.nɛl.mɑ̃/', syllables: ['an', 'ti', 'cons', 'ti', 'tu', 'tio', 'nelle', 'ment'], focus: 'Le record français', hint: "Le mot le plus long du français courant. Lis lentement d'abord, puis accélère progressivement." },
  { word: 'incompréhensible', ipa: '/ɛ̃.kɔ̃.pʁe.ɑ̃.sibl/', syllables: ['in', 'com', 'pré', 'hen', 'sible'], focus: 'Trois nasaux !', hint: "« in », « com », « hen » : trois nasaux différents. Garde la résonance fluide." },
  { word: 'vraisemblablement', ipa: '/vʁɛ.sɑ̃.blɑ̃.blə.mɑ̃/', syllables: ['vrai', 'sem', 'bla', 'ble', 'ment'], focus: 'Trois « bl » + nasaux', hint: "Les groupes « bl » s'enchaînent sans pause. Termine sur le nasal final." },
  { word: 'institutionnalisation', ipa: '/ɛ̃s.ti.ty.sjɔ.na.li.za.sjɔ̃/', syllables: ['ins', 'ti', 'tu', 'tio', 'na', 'li', 'sa', 'tion'], focus: '8 syllabes — marathon', hint: "Découpe en 4+4. Respire au milieu si nécessaire au début, puis vise un seul souffle." },
  { word: 'disproportionné', ipa: '/dis.pʁɔ.pɔʁ.sjɔ.ne/', syllables: ['dis', 'pro', 'por', 'tio', 'nné'], focus: 'Trois « por/pro »', hint: "Trois syllabes très proches sonorement. Articule clairement sans confondre les « r »." },
  { word: 'intergouvernemental', ipa: '/ɛ̃.tɛʁ.ɡu.vɛʁ.nə.mɑ̃.tal/', syllables: ['in', 'ter', 'gou', 'ver', 'ne', 'men', 'tal'], focus: 'Lexique politique', hint: "Mot du vocabulaire diplomatique. Les deux « r » restent légers, le nasal central porte le rythme." },
  { word: 'responsabilisation', ipa: '/ʁɛs.pɔ̃.sa.bi.li.za.sjɔ̃/', syllables: ['res', 'pon', 'sa', 'bi', 'li', 'sa', 'tion'], focus: 'Nasaux d\'encadrement', hint: "Un nasal au début, un nasal à la fin. Comme deux parenthèses sonores qui tiennent le mot." },
  { word: 'multidimensionnel', ipa: '/myl.ti.di.mɑ̃.sjɔ.nɛl/', syllables: ['mul', 'ti', 'di', 'men', 'sio', 'nnel'], focus: '« u » serré + nasal central', hint: "Le « u » initial demande des lèvres serrées. Le nasal « men » est le pivot du mot." },
];

const B2_FEATURED_SENTENCES = [
  // Activity 2 — Fluidité & vitesse
  { id: 'b2-s1', sentence: 'Les transports internationaux deviennent progressivement plus écologiques.', focus: 'Fluidité · vitesse', hint: "Phrase journalistique. Vise le débit naturel — pas de pause artificielle au milieu.", context: 'Actualité environnementale' },
  { id: 'b2-s2', sentence: 'Cette décision gouvernementale reste extrêmement controversée.', focus: 'Vocabulaire politique', hint: "Articule clairement « gouvernementale » et « controversée » — deux mots-clés à valoriser.", context: 'Débat politique' },
  { id: 'b2-s3', sentence: 'Nous avons malheureusement sous-estimé les conséquences économiques.', focus: 'Phrase complexe · regret', hint: "Ton un peu désolé sur « malheureusement ». Garde le rythme régulier.", context: 'Bilan professionnel' },
  { id: 'b2-s4', sentence: 'Leur comportement semblait totalement incompréhensible dans ce contexte.', focus: 'Adverbes longs', hint: "Trois mots-clés : « totalement », « incompréhensible », « contexte ». Pas d'arrêt.", context: 'Analyse sociale' },
  { id: 'b2-s5', sentence: "Plusieurs journalistes ont dénoncé un manque de transparence politique.", focus: 'Liaison + nasaux', hint: "Liaison « journalistes_ont ». Mets de l'autorité dans la voix.", context: 'Critique médiatique' },
  // Activity 4 — Intonation naturelle avancée
  { id: 'b2-s6', sentence: 'Ah bon ? Tu trouves vraiment cette solution efficace ?', focus: 'Émotion · scepticisme ↗', hint: "« Ah bon ? » légèrement ironique. La voix monte fort à la fin pour exprimer le doute.", context: 'Réaction sceptique' },
  { id: 'b2-s7', sentence: 'Franchement, cette présentation était remarquablement claire.', focus: 'Émotion · admiration', hint: "Pause après « franchement ». Donne du poids à « remarquablement ».", context: 'Compliment professionnel' },
  { id: 'b2-s8', sentence: 'Je dois avouer que cette situation devient légèrement frustrante…', focus: 'Émotion · irritation contenue', hint: "Ton diplomatique mais ferme. Traîne sur « légèrement » avec une petite tension.", context: 'Tension professionnelle' },
  { id: 'b2-s9', sentence: "Honnêtement, je ne m'attendais absolument pas à une telle réaction.", focus: 'Émotion · surprise sincère', hint: "« Absolument » porte tout le poids émotionnel. Voix qui descend ensuite.", context: 'Conversation sincère' },
  { id: 'b2-s10', sentence: "C'est précisément ce genre d'initiative dont nous avions besoin.", focus: 'Émotion · enthousiasme posé', hint: "Ton mature et convaincu. Articule « précisément » sans le précipiter.", context: 'Validation professionnelle' },
  // Activity 6 — Prononciation académique
  { id: 'b2-s11', sentence: 'Les transformations technologiques influencent profondément les comportements sociaux contemporains.', focus: 'Discours académique', hint: "Phrase de conférence. Pacing posé, articulation soignée.", context: 'Discours universitaire' },
  { id: 'b2-s12', sentence: 'Cette conférence internationale abordera plusieurs problématiques environnementales majeures.', focus: 'Lexique formel', hint: "Quatre adjectifs longs. Garde le souffle pour tout enchaîner.", context: 'Annonce officielle' },
  { id: 'b2-s13', sentence: 'La mondialisation économique entraîne des conséquences particulièrement complexes.', focus: 'Vocabulaire abstrait', hint: "« mondialisation » et « particulièrement » : articule lentement la première fois.", context: 'Analyse économique' },
  { id: 'b2-s14', sentence: 'Plusieurs chercheurs remettent actuellement en question cette théorie scientifique.', focus: 'Discours scientifique', hint: "« remettent_actuellement » : liaison naturelle. Ton informé.", context: 'Vulgarisation scientifique' },
  { id: 'b2-s15', sentence: 'Le gouvernement souhaite renforcer la coopération interinstitutionnelle.', focus: 'Vocabulaire institutionnel', hint: "« interinstitutionnelle » : mot très long. Découpe : inter-insti-tu-tion-nelle.", context: 'Annonce politique' },
  // Activity 7 — Débats
  { id: 'b2-s16', sentence: 'Les réseaux sociaux améliorent-ils réellement la communication ?', focus: 'Débat · prends position', hint: "Réponds 60-90 secondes. Donne 2 raisons, 1 contre-exemple, une conclusion.", context: 'Mini débat oral B2' },
  { id: 'b2-s17', sentence: "Le télétravail représente-t-il l'avenir du monde professionnel ?", focus: 'Débat · argumente', hint: "Adopte une position nuancée. Utilise « cela dit », « toutefois », « en revanche ».", context: 'Mini débat oral B2' },
  { id: 'b2-s18', sentence: 'La technologie réduit-elle les interactions humaines authentiques ?', focus: 'Débat · réfléchis', hint: "Question philosophique. Pose ton ton, parle avec maturité.", context: 'Mini débat oral B2' },
  // Activity 9 — Français parlé naturel (réductions)
  { id: 'b2-s19', sentence: "Chais pas si on aura le temps.", focus: 'Français parlé · « chais pas »', hint: "« je ne sais pas » devient « chais pas » à l'oral. Très naturel entre amis.", context: 'Registre informel — entre amis' },
  { id: 'b2-s20', sentence: "T'es déjà allé à Montréal ?", focus: "Français parlé · « t'es »", hint: "« tu es » se réduit en « t'es ». Très fréquent à l'oral familier.", context: 'Registre informel — conversation' },
  { id: 'b2-s21', sentence: "Y a beaucoup de monde aujourd'hui.", focus: 'Français parlé · « ya »', hint: "« il y a » devient « y a » presque toujours à l'oral. À écrire « il y a ».", context: 'Registre informel — observation' },
  { id: 'b2-s22', sentence: "Chuis vraiment fatigué après cette journée.", focus: 'Français parlé · « chuis »', hint: "« je suis » se contracte en « chuis » entre amis. Garde-le pour l'oral.", context: 'Registre informel — confidence' },
  // Activity 10 — Simulation immersive professionnelle (4-line dialogue)
  { id: 'b2-s23', sentence: "— Merci à tous d'être présents aujourd'hui pour cette réunion stratégique.", focus: 'Dialogue · ouverture pro', hint: "Ton de leadership. Pause après « Merci à tous ». Articule « stratégique » clairement.", context: 'Dialogue pro · le manager' },
  { id: 'b2-s24', sentence: "— Nous devons absolument améliorer la transparence des données environnementales.", focus: 'Dialogue · prise de position', hint: "Insiste sur « absolument ». Ton convaincu, déterminé.", context: 'Dialogue pro · l\'expert' },
  { id: 'b2-s25', sentence: '— Plusieurs partenaires internationaux souhaitent également accélérer les transports durables.', focus: 'Dialogue · information', hint: "Trois liaisons. Ton informatif, presque diplomatique.", context: 'Dialogue pro · l\'analyste' },
  { id: 'b2-s26', sentence: '— Dans ce cas, il faudrait probablement revoir notre calendrier initial.', focus: 'Dialogue · proposition', hint: "Ton mesuré et constructif. « Dans ce cas » introduit une solution.", context: 'Dialogue pro · la conclusion' },
  // Selected from the 10 advanced sentences (5)
  { id: 'b2-s27', sentence: 'La transparence administrative constitue un enjeu fondamental dans les démocraties modernes.', focus: 'Discours civique', hint: "Phrase éditoriale. Articulation très claire, pacing régulier.", context: 'Analyse politique' },
  { id: 'b2-s28', sentence: "Plusieurs spécialistes considèrent cette décision économique comme totalement disproportionnée.", focus: 'Adverbe long · contexte économique', hint: "« totalement disproportionnée » : valorise les deux mots-clés.", context: 'Critique économique' },
  { id: 'b2-s29', sentence: 'La coopération intergouvernementale devient indispensable face aux crises internationales.', focus: 'Mots savants en chaîne', hint: "Phrase typique des journaux télévisés. Le rythme est tout.", context: 'Géopolitique' },
  { id: 'b2-s30', sentence: "Son raisonnement semblait particulièrement pertinent dans ce contexte politique sensible.", focus: 'Évaluation nuancée', hint: "Pause subtile avant « particulièrement ». Ton appréciatif.", context: 'Commentaire sur un discours' },
  { id: 'b2-s31', sentence: 'Bien que la situation soit délicate, plusieurs solutions envisageables existent encore.', focus: 'Concession + optimisme', hint: "Marque la concession après « délicate ». Reprise positive sur « solutions ».", context: 'Discours d\'espoir' },
];

const B2_DEFI_SOUNDS = [
  { sound: 'Mots à 5+ syllabes', words: ['extraordinaire', 'malheureusement', 'environnement', 'institutionnel'], hint: "Ces mots demandent un débit régulier. Ne ralentis pas en cours de route." },
  { sound: 'Voyelles fermées en succession', words: ['désherber', 'éparpiller', 'irréductible', 'professionnel'], hint: "Garde les lèvres relativement serrées sur les é/è/i successifs." },
  { sound: 'Réductions du français oral', words: ['chais pas', "t'es", 'ya', 'chuis'], hint: "Ces formes contractées sont normales à l'oral, jamais à l'écrit." },
  { sound: 'Liaisons en chaîne', words: ['les_anciens_amis', 'tout_est_arrangé', 'vous_êtes_invités', 'ils_ont_attendu'], hint: "Le souffle reste continu. Pas de coupure entre les mots." },
];

const B2_RHYTHM_SENTENCES = [
  // Activity 3 — Groupes rythmiques complexes
  { sentence: 'Malgré les nombreuses critiques formulées par les experts / le projet a finalement été approuvé.', chunks: ['Malgré les nombreuses critiques formulées par les experts', 'le projet a finalement été approuvé'] },
  { sentence: 'Pendant les négociations internationales / plusieurs désaccords importants sont apparus.', chunks: ['Pendant les négociations internationales', 'plusieurs désaccords importants sont apparus'] },
  { sentence: 'Dès que les résultats scientifiques seront publiés / les chercheurs organiseront une conférence.', chunks: ['Dès que les résultats scientifiques seront publiés', 'les chercheurs organiseront une conférence'] },
  { sentence: "Bien qu'il maîtrise parfaitement plusieurs langues étrangères / il continue à pratiquer quotidiennement.", chunks: ["Bien qu'il maîtrise parfaitement plusieurs langues étrangères", 'il continue à pratiquer quotidiennement'] },
  { sentence: "En observant attentivement les réactions du public / on comprend mieux les enjeux sociaux.", chunks: ['En observant attentivement les réactions du public', 'on comprend mieux les enjeux sociaux'] },
  // Activity 5 — Liaisons avancées (mini-phrases connectées)
  { sentence: 'ils ont immédiatement accepté', chunks: ['ils_ont_immédiatement_accepté'], focus: 'Liaisons en chaîne' },
  { sentence: 'des étudiants internationaux', chunks: ['des_étudiants_internationaux'], focus: 'Liaisons + adjectif' },
  { sentence: 'tout est absolument incroyable', chunks: ['tout_est_absolument_incroyable'], focus: 'Liaisons + adverbe' },
  { sentence: 'nous avons énormément appris', chunks: ['nous_avons_énormément_appris'], focus: 'Liaisons + participe passé' },
  { sentence: 'plusieurs anciens employés', chunks: ['plusieurs_anciens_employés'], focus: 'Liaisons + ancien' },
];

const B2_MOUTH_CARDS = [
  {
    sound: '↘',
    title: "L'intonation déclarative",
    lips: 'Détendues, presque immobiles entre les groupes',
    tongue: 'Mobile mais sans tension',
    air: 'Débit régulier, la voix descend doucement à la fin',
    examples: ['Cette décision a été prise.', 'Le projet est terminé.', "C'est exactement ce qu'il fallait."],
    mirror: 'Écoute ta propre voix : la dernière syllabe doit être plus basse que la première. C\'est la marque du français adulte.',
  },
  {
    sound: '↗',
    title: "L'intonation interrogative et expressive",
    lips: 'Légère ouverture sur la dernière syllabe',
    tongue: 'Avancée pour porter la voix',
    air: 'Souffle qui monte clairement à la fin',
    examples: ['Tu viens vraiment ?', "C'est sérieux ?", 'Ah bon ?'],
    mirror: "Pose ta main sur ta gorge : la voix monte = vibration plus haute. Une vraie question française se reconnaît à ça.",
  },
  {
    sound: 'Oral',
    title: 'Le français oral réduit',
    lips: 'Plus rapides, parfois moins articulées que l\'écrit',
    tongue: 'Centrée, position relâchée',
    air: 'Débit plus rapide, syllabes parfois fusionnées',
    examples: ['chais pas (je ne sais pas)', "t'es où (tu es où)", "y a quelqu'un (il y a quelqu'un)"],
    mirror: "Ces réductions s'entendent partout en France. Comprends-les d'abord, utilise-les avec confiance entre amis.",
  },
];

const B2_ECOUTE_PAIRS = [
  { target: 'transparence', distractor: 'transhumance', focus: 'Suffixes proches', explanation: "« transparence » = clarté, ouverture. « transhumance » = déplacement saisonnier des troupeaux. Vocabulaire très différent." },
  { target: 'adaptation', distractor: 'adoption', focus: 'Voyelles « ap » / « op »', explanation: "« adaptation » = s'ajuster (a). « adoption » = prendre comme sien (o). Sens proches mais distincts." },
  { target: 'institution', distractor: 'instruction', focus: 'Voyelles « tu » / « tru »', explanation: "« institution » = organisation établie. « instruction » = enseignement ou ordre. Contextes très différents." },
  { target: 'compréhension', distractor: 'compression', focus: 'Voyelles « pré » / « pre »', explanation: "« compréhension » = saisir un sens. « compression » = réduire physiquement. Ne confonds pas." },
  { target: 'événement', distractor: 'avènement', focus: 'Voyelles initiales é / a', explanation: "« événement » = un fait, une nouvelle. « avènement » = arrivée solennelle (royale, historique)." },
];

const dataByLevel = (level) => {
  if (level === 'B2')
    return {
      words: B2_FEATURED_WORDS,
      sentences: B2_FEATURED_SENTENCES,
      defis: B2_DEFI_SOUNDS,
      rhythm: B2_RHYTHM_SENTENCES,
      mouth: B2_MOUTH_CARDS,
      ecoute: B2_ECOUTE_PAIRS,
    };
  if (level === 'B1')
    return {
      words: B1_FEATURED_WORDS,
      sentences: B1_FEATURED_SENTENCES,
      defis: B1_DEFI_SOUNDS,
      rhythm: B1_RHYTHM_SENTENCES,
      mouth: B1_MOUTH_CARDS,
      ecoute: B1_ECOUTE_PAIRS,
    };
  if (level === 'A2')
    return {
      words: A2_FEATURED_WORDS,
      sentences: A2_FEATURED_SENTENCES,
      defis: A2_DEFI_SOUNDS,
      rhythm: A2_RHYTHM_SENTENCES,
      mouth: A2_MOUTH_CARDS,
      ecoute: A2_ECOUTE_PAIRS,
    };
  return {
    words: FEATURED_WORDS,
    sentences: FEATURED_SENTENCES,
    defis: DEFI_SOUNDS,
    rhythm: RHYTHM_SENTENCES,
    mouth: MOUTH_CARDS,
    ecoute: ECOUTE_PAIRS,
  };
};

/* -----------------------------------------------------------
   Liaison renderer — converts "petit_enfant" to "petit‿enfant" with subtle gold underscore
----------------------------------------------------------- */
const renderChunkWithLiaison = (chunk) => {
  if (!chunk.includes('_')) return chunk;
  const parts = chunk.split('_');
  return parts.map((p, i) => (
    <React.Fragment key={i}>
      {p}
      {i < parts.length - 1 && (
        <span className="text-[#D4AF37] mx-0.5" aria-hidden="true">‿</span>
      )}
    </React.Fragment>
  ));
};

/* -----------------------------------------------------------
   Simulated AI feedback
   Generates realistic but encouraging scores + per-word comments.
----------------------------------------------------------- */
function generateAIFeedback(word) {
  const base = 70 + Math.floor(Math.random() * 25); // 70–94
  const pronun = Math.min(98, Math.max(65, base + (Math.floor(Math.random() * 6) - 3)));
  const fluid = Math.min(98, Math.max(60, base + (Math.floor(Math.random() * 8) - 4)));
  const rhythm = Math.min(98, Math.max(60, base + (Math.floor(Math.random() * 8) - 4)));
  const clarity = Math.min(98, Math.max(60, base + (Math.floor(Math.random() * 6) - 3)));

  const strengths = [];
  const improvements = [];

  if (pronun > 80) strengths.push(`Bonne clarté sur « ${word.word} »`);
  if (rhythm > 78) strengths.push('Très bon rythme');
  if (fluid > 80) strengths.push('Belle fluidité');

  if (pronun < 85) improvements.push(word.hint);
  if (rhythm < 78) improvements.push('Marque mieux les syllabes pour un rythme plus régulier.');
  if (fluid < 78) improvements.push('Enchaîne les syllabes sans les détacher.');

  // Ensure at least 1 of each
  if (strengths.length === 0) strengths.push('Bonne intention vocale, continue à pratiquer.');
  if (improvements.length === 0) improvements.push('Excellente prononciation — affine encore le naturel.');

  return { pronun, fluid, rhythm, clarity, strengths, improvements };
}

/* ===========================================================
   AUDIO + RECORD primitives
=========================================================== */
function AudioButton({ text, label = 'Écouter', testId, rate = 0.95 }) {
  return (
    <button
      onClick={() => speak(text, rate)}
      className="inline-flex items-center gap-2 bg-[#1A2F4A] hover:bg-[#243a58] text-white rounded-full px-4 py-2 text-xs font-bold transition-colors"
      data-testid={testId}
    >
      <Volume2 className="w-3.5 h-3.5" />
      {label}
    </button>
  );
}

function MiniRecorder({ onRecorded, testId, submitMeta }) {
  const [recording, setRecording] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const [audioBlob, setAudioBlob] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const mrRef = useRef(null);
  const chunksRef = useRef([]);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      setSubmitted(false);
      setSubmitError('');
      mr.ondataavailable = (e) => chunksRef.current.push(e.data);
      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        const url = URL.createObjectURL(blob);
        setAudioBlob(blob);
        setAudioUrl(url);
        if (onRecorded) onRecorded(url);
        stream.getTracks().forEach((t) => t.stop());
      };
      mrRef.current = mr;
      mr.start();
      setRecording(true);
    } catch {
      alert('Microphone non disponible.');
    }
  };

  const stop = () => {
    if (mrRef.current && recording) {
      mrRef.current.stop();
      setRecording(false);
    }
  };

  const handleSubmit = async () => {
    if (!audioBlob || !submitMeta) return;
    setSubmitting(true);
    setSubmitError('');
    try {
      await uploadRecording(audioBlob, {
        level: submitMeta.level,
        exerciseId: submitMeta.exerciseId,
        exerciseTitle: submitMeta.exerciseTitle,
        promptId: submitMeta.promptId || '',
        promptText: submitMeta.promptText || '',
      });
      setSubmitted(true);
    } catch (err) {
      setSubmitError(err.message || 'Envoi impossible.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-2">
      {!recording ? (
        <button
          onClick={start}
          className="inline-flex items-center gap-2 bg-white border-2 border-[#1A2F4A] text-[#1A2F4A] rounded-full px-4 py-2 text-xs font-bold hover:bg-[#FAF7F0] transition-colors"
          data-testid={testId}
        >
          <Mic className="w-3.5 h-3.5" /> Enregistre-toi
        </button>
      ) : (
        <button
          onClick={stop}
          className="inline-flex items-center gap-2 bg-red-500 text-white rounded-full px-4 py-2 text-xs font-bold animate-pulse"
          data-testid={`${testId}-stop`}
        >
          <Square className="w-3.5 h-3.5" /> Arrêter
        </button>
      )}
      {audioUrl && (
        <audio controls src={audioUrl} className="w-full h-8" data-testid={`${testId}-playback`} />
      )}
      {audioBlob && submitMeta && (
        submitted ? (
          <div className="text-[11px] text-emerald-700 font-bold" data-testid={`${testId}-sent`}>✓ Envoyé à ton professeur</div>
        ) : (
          <div className="space-y-1">
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="inline-flex items-center gap-1.5 bg-[#D4AF37] hover:bg-[#1A2F4A] text-[#1A2F4A] hover:text-[#D4AF37] rounded-full px-3 py-1.5 text-[11px] font-bold transition-colors disabled:opacity-50"
              data-testid={`${testId}-send`}
            >
              <Send className="w-3 h-3" />
              {submitting ? 'Envoi…' : 'Envoyer au professeur'}
            </button>
            {submitError && <p className="text-[10px] text-red-600" data-testid={`${testId}-error`}>{submitError}</p>}
          </div>
        )
      )}
    </div>
  );
}

/* ===========================================================
   FEEDBACK PANEL
=========================================================== */
function FeedbackPanel({ feedback, onSave, saveTestId }) {
  if (!feedback) return null;
  const Bar = ({ label, value }) => (
    <div>
      <div className="flex justify-between text-[10px] text-[#6B4E37] mb-1 font-medium">
        <span>{label}</span>
        <span className="text-[#1A2F4A] font-bold">{value}%</span>
      </div>
      <div className="w-full h-1.5 bg-[#F3F4F6] rounded-full overflow-hidden">
        <div
          className="h-full rounded-full bg-[#D4AF37] transition-all duration-700"
          style={{ width: `${value}%` }}
        />
      </div>
    </div>
  );

  return (
    <div className="bg-[#1A2F4A] rounded-2xl p-5 space-y-4" data-testid="feedback-panel">
      <div className="flex items-center gap-2">
        <Sparkles className="w-4 h-4 text-[#D4AF37]" />
        <p className="text-[10px] text-[#D4AF37] uppercase tracking-wider font-bold">Analyse IA</p>
      </div>

      <div className="space-y-3">
        <Bar label="Prononciation" value={feedback.pronun} />
        <Bar label="Fluidité" value={feedback.fluid} />
        <Bar label="Rythme" value={feedback.rhythm} />
        <Bar label="Clarté" value={feedback.clarity} />
      </div>

      <div className="space-y-2">
        {feedback.strengths.map((s, i) => (
          <div key={`s-${i}`} className="flex items-start gap-2 text-xs text-white/90">
            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0 mt-0.5" />
            <span>{s}</span>
          </div>
        ))}
        {feedback.improvements.map((s, i) => (
          <div key={`i-${i}`} className="flex items-start gap-2 text-xs text-white/90">
            <AlertCircle className="w-3.5 h-3.5 text-amber-400 flex-shrink-0 mt-0.5" />
            <span>{s}</span>
          </div>
        ))}
      </div>

      {onSave && (
        <button
          onClick={onSave}
          className="w-full mt-2 inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-[#1A2F4A] rounded-full px-4 py-2.5 text-xs font-bold hover:bg-white transition-colors"
          data-testid={saveTestId}
        >
          <Save className="w-3.5 h-3.5" />
          Sauvegarder ma tentative
        </button>
      )}
    </div>
  );
}

/* ===========================================================
   1️⃣ PRONONCE & COMPARE — featured 6 words
=========================================================== */
function WordCard({ word, onSave, level = 'A1' }) {
  const [feedback, setFeedback] = useState(null);

  return (
    <div
      className="bg-white border border-[#E8E4DC] rounded-2xl p-5 space-y-4 shadow-sm"
      data-testid={`word-${word.word}`}
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <p
            className="text-2xl font-bold text-[#1A2F4A]"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {word.word}
          </p>
          <p className="text-[11px] text-[#6B4E37] mt-1 font-mono tracking-wide">{word.ipa}</p>
        </div>
        <span className="text-[9px] bg-[#D4AF37]/10 text-[#6B4E37] rounded-full px-2.5 py-1 font-bold uppercase tracking-wider flex-shrink-0">
          {word.focus}
        </span>
      </div>

      {/* Syllable segmentation */}
      <div className="flex flex-wrap items-center gap-1.5">
        {word.syllables.map((s, i) => (
          <React.Fragment key={i}>
            <span className="bg-[#FAF7F0] border border-[#E8E4DC] text-[#1A2F4A] rounded-lg px-3 py-1.5 text-sm font-medium">
              {s}
            </span>
            {i < word.syllables.length - 1 && (
              <span className="text-[#D4AF37] text-xs">·</span>
            )}
          </React.Fragment>
        ))}
      </div>

      <div className="bg-[#FAF7F0] border-l-2 border-[#D4AF37] rounded-r-lg px-3 py-2">
        <p className="text-[11px] text-[#6B4E37] leading-relaxed italic">💡 {word.hint}</p>
      </div>

      <div className="flex flex-wrap gap-2">
        <AudioButton text={word.word} testId={`listen-${word.word}`} />
        <MiniRecorder
          testId={`record-${word.word}`}
          onRecorded={() => setFeedback(generateAIFeedback(word))}
          submitMeta={{
            level,
            exerciseId: `sons-du-francais-${level.toLowerCase()}-${word.word}`,
            exerciseTitle: `Les sons du français — ${word.word} (${level})`,
            promptText: word.word,
          }}
        />
      </div>

      <FeedbackPanel
        feedback={feedback}
        onSave={feedback ? () => onSave({ ...feedback, word: word.word }) : null}
        saveTestId={`save-${word.word}`}
      />
    </div>
  );
}

function PrononceCompareModule({ level, onSave }) {
  const { words, sentences } = dataByLevel(level);
  const headers = {
    A1: {
      eyebrow: 'Défi A1 — Les mots qui chantent',
      line: 'Travaille le rythme et les sons du français.',
      sub: '6 mots vivants choisis pour leur sonorité. Écoute, observe les syllabes, enregistre-toi et reçois une analyse instantanée de ta prononciation.',
    },
    A2: {
      eyebrow: 'Défi A2 — Les mots du quotidien',
      line: 'Mots et phrases plus fluides, intonation plus naturelle.',
      sub: `${words.length} mots fréquents + ${sentences.length} phrases naturelles (intonation, dialogue, défi rapide). Écoute, enregistre, reçois ton analyse instantanée.`,
    },
    B1: {
      eyebrow: 'Défi B1 — Sonner vraiment français',
      line: 'Mots longs, phrases expressives, vrai débit naturel.',
      sub: `${words.length} mots multisyllabiques + ${sentences.length} phrases riches : enchaînements, émotions, débats et mini-dialogue immersif. Vise la fluidité, pas la perfection.`,
    },
    B2: {
      eyebrow: 'Défi B2 — Le français pro & soutenu',
      line: 'Vocabulaire savant, rythme naturel, intonation maîtrisée.',
      sub: `${words.length} mots redoutables + ${sentences.length} phrases avancées : discours académique, débats, dialogues pro et français parlé naturel. Active la vitesse rapide pour t'entraîner à comprendre les natifs.`,
    },
  };
  const h = headers[level] || headers.A1;

  return (
    <div className="space-y-5">
      {/* Featured intro */}
      <div className="bg-gradient-to-br from-[#1A2F4A] to-[#243a58] rounded-2xl p-6 text-white" data-testid="featured-intro">
        <div className="flex items-center gap-2 mb-2">
          <Target className="w-4 h-4 text-[#D4AF37]" />
          <p className="text-[10px] text-[#D4AF37] uppercase tracking-wider font-bold">
            {h.eyebrow}
          </p>
        </div>
        <p
          className="text-lg font-bold mb-2"
          style={{ fontFamily: "'Playfair Display', serif" }}
        >
          {h.line}
        </p>
        <p className="text-xs text-white/70 leading-relaxed">{h.sub}</p>
      </div>

      {words.map((w) => (
        <WordCard key={w.word} word={w} onSave={onSave} level={level} />
      ))}

      {/* Sentences section divider */}
      <div className="pt-6" data-testid="sentences-intro">
        <div className="flex items-center justify-center gap-3 mb-4">
          <div className="h-px flex-1 bg-[#D4AF37]/40" />
          <span className="text-[10px] text-[#6B4E37] uppercase tracking-[0.25em] font-bold">
            🗣️ {level === 'B2' ? 'Phrases avancées' : level === 'B1' ? 'Phrases expressives' : 'Petites Phrases'} — {level}
          </span>
          <div className="h-px flex-1 bg-[#D4AF37]/40" />
        </div>
        <p className="text-xs text-[#6B4E37] italic text-center leading-relaxed max-w-md mx-auto">
          {level === 'B2'
            ? "Phrases longues, débats, dialogues pro et français parlé naturel. Active la vitesse rapide pour entraîner ton oreille au français des natifs."
            : level === 'B1'
            ? "Phrases riches en sons, en émotion et en rythme. Tu trouveras aussi des prompts de mini-débat et un dialogue d'immersion."
            : level === 'A2'
            ? "Des phrases riches en sons nasaux, en intonation et en liaisons. Quelques-unes sont des mini-dialogues pour t'immerger dans une vraie scène."
            : "Des phrases courtes que tu utiliseras vraiment. Écoute le rythme, observe les liaisons, et essaie de répéter avec la même musique."}
        </p>
      </div>

      {sentences.map((s) => (
        <SentenceCard key={s.id} item={s} onSave={onSave} level={level} />
      ))}
    </div>
  );
}

function SentenceCard({ item, onSave, level = 'A1' }) {
  const [feedback, setFeedback] = useState(null);

  return (
    <div
      className="bg-white border border-[#E8E4DC] rounded-2xl p-5 space-y-4 shadow-sm"
      data-testid={`sentence-${item.id}`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <p
            className="text-xl font-bold text-[#1A2F4A] leading-snug"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {item.sentence}
          </p>
          <p className="text-[10px] text-[#6B4E37] mt-1.5 uppercase tracking-wider font-semibold">
            {item.context}
          </p>
        </div>
        <span className="text-[9px] bg-[#D4AF37]/10 text-[#6B4E37] rounded-full px-2.5 py-1 font-bold uppercase tracking-wider flex-shrink-0">
          {item.focus}
        </span>
      </div>

      <div className="bg-[#FAF7F0] border-l-2 border-[#D4AF37] rounded-r-lg px-3 py-2">
        <p className="text-[11px] text-[#6B4E37] leading-relaxed italic">💡 {item.hint}</p>
      </div>

      <div className="flex flex-wrap gap-2">
        <AudioButton text={item.sentence} testId={`sentence-listen-${item.id}`} />
        <MiniRecorder
          testId={`sentence-record-${item.id}`}
          onRecorded={() =>
            setFeedback(generateAIFeedback({ word: item.sentence, hint: item.hint }))
          }
          submitMeta={{
            level,
            exerciseId: `sons-du-francais-${level.toLowerCase()}-${item.id}`,
            exerciseTitle: `Les sons du français — Phrase (${level})`,
            promptId: String(item.id),
            promptText: item.sentence,
          }}
        />
      </div>

      <FeedbackPanel
        feedback={feedback}
        onSave={feedback ? () => onSave({ ...feedback, word: item.sentence }) : null}
        saveTestId={`sentence-save-${item.id}`}
      />
    </div>
  );
}

/* ===========================================================
   2️⃣ DÉFIS DE PRONONCIATION
=========================================================== */
function DefisModule({ level }) {
  const { defis } = dataByLevel(level);
  return (
    <div className="space-y-4">
      {defis.map((d) => (
        <div
          key={d.sound}
          className="bg-white border border-[#E8E4DC] rounded-2xl p-5 space-y-3"
          data-testid={`defi-${d.sound}`}
        >
          <div className="flex items-center justify-between gap-3">
            <p
              className="text-base font-bold text-[#1A2F4A]"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {d.sound}
            </p>
            <span className="text-[9px] bg-[#D4AF37]/10 text-[#6B4E37] rounded-full px-2.5 py-1 font-bold uppercase tracking-wider">
              4 mots
            </span>
          </div>

          <div className="bg-[#FAF7F0] border-l-2 border-[#D4AF37] rounded-r-lg px-3 py-2">
            <p className="text-[11px] text-[#6B4E37] leading-relaxed italic">💡 {d.hint}</p>
          </div>

          <div className="grid grid-cols-2 gap-2">
            {d.words.map((w) => (
              <button
                key={w}
                onClick={() => speak(w)}
                className="bg-[#FAF7F0] border border-[#E8E4DC] hover:border-[#D4AF37] rounded-lg px-3 py-2.5 text-left transition-colors group"
                data-testid={`defi-word-${w}`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-[#1A2F4A]">{w}</span>
                  <Volume2 className="w-3 h-3 text-[#6B4E37] group-hover:text-[#D4AF37] transition-colors" />
                </div>
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ===========================================================
   3️⃣ RYTHME & FLUIDITÉ
=========================================================== */
function RythmeModule({ level, onSave }) {
  const { rhythm } = dataByLevel(level);
  const [feedbacks, setFeedbacks] = useState({});

  return (
    <div className="space-y-4">
      {rhythm.map((r, i) => (
        <div
          key={i}
          className="bg-white border border-[#E8E4DC] rounded-2xl p-5 space-y-4"
          data-testid={`rythme-${i}`}
        >
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Music className="w-3.5 h-3.5 text-[#D4AF37]" />
              <p className="text-[10px] text-[#D4AF37] uppercase tracking-wider font-bold">
                {r.focus || 'Groupe de souffle'}
              </p>
            </div>
            {r.focus && (
              <span className="text-[9px] bg-[#D4AF37]/10 text-[#6B4E37] rounded-full px-2.5 py-1 font-bold uppercase tracking-wider">
                A2
              </span>
            )}
          </div>

          {/* Animated rhythm chunks */}
          <div className="flex flex-wrap items-center gap-2">
            {r.chunks.map((c, j) => (
              <React.Fragment key={j}>
                <div
                  className="bg-[#1A2F4A] text-white rounded-xl px-4 py-3 shadow-sm"
                  style={{
                    animation: `pulse-rhythm 2s ease-in-out ${j * 0.3}s infinite`,
                  }}
                >
                  <span className="text-sm font-medium" style={{ fontFamily: "'Playfair Display', serif" }}>
                    {renderChunkWithLiaison(c)}
                  </span>
                </div>
                {j < r.chunks.length - 1 && (
                  <span className="text-[#D4AF37] text-lg font-bold">/</span>
                )}
              </React.Fragment>
            ))}
          </div>

          <p className="text-[11px] text-[#6B4E37] italic">
            {r.focus
              ? 'Le symbole ‿ marque une liaison : enchaîne les deux mots sans coupure.'
              : "Lis chaque groupe d'un seul souffle, sans pause au milieu."}
          </p>

          <div className="flex flex-wrap gap-2">
            <AudioButton text={r.sentence.replace(/\//g, ' ').replace(/_/g, ' ')} testId={`rythme-listen-${i}`} />
            <MiniRecorder
              testId={`rythme-record-${i}`}
              onRecorded={() =>
                setFeedbacks((p) => ({
                  ...p,
                  [i]: generateAIFeedback({ word: r.sentence, hint: r.focus || 'Marque bien chaque groupe de souffle.' }),
                }))
              }
              submitMeta={{
                level,
                exerciseId: `sons-du-francais-${level.toLowerCase()}-rythme-${i}`,
                exerciseTitle: `Les sons du français — Rythme (${level})`,
                promptId: String(i),
                promptText: r.sentence,
              }}
            />
          </div>

          {feedbacks[i] && (
            <FeedbackPanel
              feedback={feedbacks[i]}
              onSave={() => onSave({ ...feedbacks[i], word: r.sentence })}
              saveTestId={`rythme-save-${i}`}
            />
          )}
        </div>
      ))}

      <style>{`
        @keyframes pulse-rhythm {
          0%, 100% { transform: translateY(0); opacity: 1; }
          50% { transform: translateY(-3px); opacity: 0.85; }
        }
      `}</style>
    </div>
  );
}

/* ===========================================================
   4️⃣ BOUCHE & PRONONCIATION
=========================================================== */
function BoucheModule({ level }) {
  const { mouth } = dataByLevel(level);
  return (
    <div className="space-y-4">
      {mouth.map((m) => (
        <div
          key={m.sound}
          className="bg-white border border-[#E8E4DC] rounded-2xl p-5 space-y-4"
          data-testid={`bouche-${m.sound}`}
        >
          <div className="flex items-center justify-between gap-3">
            <div>
              <p
                className="text-base font-bold text-[#1A2F4A]"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                {m.title}
              </p>
              <p className="text-[11px] text-[#6B4E37] mt-0.5">
                Son cible : <span className="font-mono font-bold text-[#1A2F4A]">« {m.sound} »</span>
              </p>
            </div>
            {/* Mouth icon */}
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#D4AF37]/20 to-[#FAF7F0] border border-[#D4AF37]/40 flex items-center justify-center flex-shrink-0">
              <Smile className="w-5 h-5 text-[#D4AF37]" />
            </div>
          </div>

          {/* Articulation guide */}
          <div className="space-y-2">
            <div className="flex items-start gap-2 text-xs text-[#1A2F4A]">
              <span className="text-[#D4AF37] mt-0.5">●</span>
              <span><b className="font-bold">Lèvres :</b> {m.lips}</span>
            </div>
            <div className="flex items-start gap-2 text-xs text-[#1A2F4A]">
              <span className="text-[#D4AF37] mt-0.5">●</span>
              <span><b className="font-bold">Langue :</b> {m.tongue}</span>
            </div>
            <div className="flex items-start gap-2 text-xs text-[#1A2F4A]">
              <Wind className="w-3 h-3 text-[#D4AF37] mt-0.5 flex-shrink-0" />
              <span><b className="font-bold">Souffle :</b> {m.air}</span>
            </div>
          </div>

          {/* Mirror practice */}
          <div className="bg-[#FAF7F0] border-l-2 border-[#D4AF37] rounded-r-lg px-3 py-2">
            <p className="text-[11px] text-[#6B4E37] leading-relaxed italic">🪞 {m.mirror}</p>
          </div>

          {/* Examples */}
          <div className="flex flex-wrap gap-2">
            {m.examples.map((ex) => (
              <button
                key={ex}
                onClick={() => speak(ex)}
                className="inline-flex items-center gap-1.5 bg-[#FAF7F0] border border-[#E8E4DC] hover:border-[#D4AF37] rounded-full px-3 py-1.5 text-xs text-[#1A2F4A] font-medium transition-colors"
              >
                <Play className="w-2.5 h-2.5 text-[#D4AF37]" />
                {ex}
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ===========================================================
   5️⃣ ÉCOUTE ACTIVE — listening discrimination
=========================================================== */
function EcouteModule({ level }) {
  const { ecoute } = dataByLevel(level);
  const [revealed, setRevealed] = useState({});
  const [choices, setChoices] = useState({});

  const handlePick = (i, pick, target) => {
    setChoices((p) => ({ ...p, [i]: pick }));
    setRevealed((p) => ({ ...p, [i]: pick === target }));
  };

  return (
    <div className="space-y-4">
      {ecoute.map((p, i) => {
        const isAnswered = choices[i] !== undefined;
        const isCorrect = revealed[i];
        return (
          <div
            key={i}
            className="bg-white border border-[#E8E4DC] rounded-2xl p-5 space-y-4"
            data-testid={`ecoute-${i}`}
          >
            <div className="flex items-center gap-2">
              <Ear className="w-3.5 h-3.5 text-[#D4AF37]" />
              <p className="text-[10px] text-[#D4AF37] uppercase tracking-wider font-bold">
                {p.focus}
              </p>
            </div>

            <p
              className="text-sm text-[#1A2F4A] font-medium"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Quel mot entends-tu ?
            </p>

            <button
              onClick={() => speak(p.target)}
              className="w-full inline-flex items-center justify-center gap-2 bg-[#1A2F4A] hover:bg-[#243a58] text-white rounded-full py-3 text-sm font-bold transition-colors"
              data-testid={`ecoute-play-${i}`}
            >
              <Volume2 className="w-4 h-4" />
              Écouter
            </button>

            <div className="grid grid-cols-2 gap-2">
              {[p.target, p.distractor]
                .sort(() => (i % 2 === 0 ? -1 : 1)) // stable per-row
                .map((opt) => {
                  const picked = choices[i] === opt;
                  const showCorrect = isAnswered && opt === p.target;
                  const showWrong = isAnswered && picked && opt !== p.target;
                  return (
                    <button
                      key={opt}
                      onClick={() => !isAnswered && handlePick(i, opt, p.target)}
                      disabled={isAnswered}
                      className={`rounded-xl px-4 py-3 text-sm font-bold transition-all ${
                        showCorrect
                          ? 'bg-emerald-50 border-2 border-emerald-300 text-emerald-700'
                          : showWrong
                          ? 'bg-amber-50 border-2 border-amber-300 text-amber-700'
                          : 'bg-white border border-[#E8E4DC] text-[#1A2F4A] hover:border-[#D4AF37]'
                      }`}
                      data-testid={`ecoute-pick-${i}-${opt}`}
                    >
                      {opt}
                    </button>
                  );
                })}
            </div>

            {isAnswered && (
              <div
                className={`rounded-xl px-4 py-3 ${
                  isCorrect
                    ? 'bg-emerald-50 border border-emerald-200'
                    : 'bg-amber-50 border border-amber-200'
                }`}
                data-testid={`ecoute-feedback-${i}`}
              >
                <p
                  className={`text-xs font-bold mb-1 ${
                    isCorrect ? 'text-emerald-700' : 'text-amber-700'
                  }`}
                >
                  {isCorrect ? '✓ Très bien !' : `La bonne réponse était « ${p.target} ».`}
                </p>
                <p className="text-[11px] text-[#6B4E37] leading-relaxed italic">{p.explanation}</p>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ===========================================================
   HISTORY — localStorage of saved attempts
=========================================================== */
const HISTORY_KEY = 'selora_sons_history';

function loadHistory() {
  try {
    const raw = localStorage.getItem(HISTORY_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveAttempt(entry) {
  const history = loadHistory();
  const next = [
    {
      ...entry,
      ts: new Date().toISOString(),
      id: Math.random().toString(36).slice(2, 9),
    },
    ...history,
  ].slice(0, 10); // keep last 10
  localStorage.setItem(HISTORY_KEY, JSON.stringify(next));
  return next;
}

function HistoryPanel({ history, onClear }) {
  if (!history.length) return null;
  return (
    <div className="bg-white border border-[#E8E4DC] rounded-2xl p-5 mt-6" data-testid="history-panel">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <History className="w-4 h-4 text-[#D4AF37]" />
          <p className="text-[10px] text-[#D4AF37] uppercase tracking-wider font-bold">
            Mes tentatives ({history.length})
          </p>
        </div>
        <button
          onClick={onClear}
          className="text-[10px] text-[#6B4E37] hover:text-red-500 transition-colors inline-flex items-center gap-1"
          data-testid="history-clear"
        >
          <Trash2 className="w-3 h-3" />
          Effacer
        </button>
      </div>

      <div className="space-y-2">
        {history.map((h) => (
          <div
            key={h.id}
            className="bg-[#FAF7F0] border border-[#E8E4DC] rounded-lg px-3 py-2 flex items-center justify-between"
          >
            <div className="flex-1 min-w-0">
              <p
                className="text-sm font-bold text-[#1A2F4A] truncate"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                {h.word}
              </p>
              <p className="text-[10px] text-[#6B4E37] mt-0.5">
                {new Date(h.ts).toLocaleString('fr-FR')}
              </p>
            </div>
            <div className="text-right ml-3 flex-shrink-0">
              <p className="text-base font-bold text-[#D4AF37]">{h.pronun}%</p>
              <p className="text-[9px] text-[#6B4E37] uppercase tracking-wider">prononciation</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ===========================================================
   MAIN PAGE
=========================================================== */
export default function LesSonsDuFrancais() {
  const [active, setActive] = useState('prononce');
  const [level, setLevel] = useState('A1');
  const [history, setHistory] = useState([]);
  const [speed, setSpeed] = useState('native');

  useEffect(() => {
    setHistory(loadHistory());
  }, []);

  // Apply the selected playback rate (also reset to native when leaving B2)
  useEffect(() => {
    const rates = { slow: 0.7, native: 0.95, fast: 1.2 };
    setPlaybackRate(rates[speed] || 0.95);
  }, [speed]);

  useEffect(() => {
    if (level !== 'B2' && speed !== 'native') setSpeed('native');
  }, [level, speed]);

  const handleSave = (entry) => {
    setHistory(saveAttempt(entry));
  };

  const handleClear = () => {
    localStorage.removeItem(HISTORY_KEY);
    setHistory([]);
  };

  return (
    <div className="bg-[#FAF7F0] min-h-screen">
      {/* Header */}
      <section className="pt-12 pb-6">
        <div className="max-w-[600px] mx-auto px-6">
          <div
            className="flex items-center gap-1.5 text-[10px] text-[#999] mb-6 flex-wrap"
            data-testid="breadcrumb"
          >
            <Link to="/speaking-lab" className="hover:text-[#D4AF37] transition-colors">
              Speaking Lab
            </Link>
            <ChevronRight className="w-3 h-3" />
            <span>Atelier d'Expression</span>
            <ChevronRight className="w-3 h-3" />
            <span className="text-[#1A2F4A] font-bold">Les Sons du Français</span>
          </div>

          <div className="text-center mb-6">
            <div className="inline-flex items-center gap-3 mb-3">
              <div className="h-px w-10 bg-[#D4AF37]/50" />
              <Volume2 className="w-5 h-5 text-[#D4AF37]" />
              <div className="h-px w-10 bg-[#D4AF37]/50" />
            </div>
            <h1
              className="text-3xl sm:text-4xl font-bold text-[#1A2F4A] mb-3"
              style={{ fontFamily: "'Playfair Display', serif" }}
              data-testid="page-title"
            >
              Les Sons du Français
            </h1>
            <p className="text-sm text-[#6B4E37] italic max-w-md mx-auto leading-relaxed">
              Un espace immersif pour explorer les sons, le rythme et la musicalité du français.
            </p>
            <div className="flex justify-center mt-4 gap-2" data-testid="level-tabs">
              {['A1', 'A2', 'B1', 'B2'].map((lv) => (
                <button
                  key={lv}
                  onClick={() => setLevel(lv)}
                  className={`text-[11px] font-bold tracking-wider rounded-full px-4 py-1.5 transition-all ${
                    level === lv
                      ? 'bg-[#1A2F4A] text-white shadow-sm'
                      : 'bg-white text-[#1A2F4A] border border-[#E8E4DC] hover:border-[#D4AF37]'
                  }`}
                  data-testid={`level-tab-${lv}`}
                >
                  {lv}
                </button>
              ))}
            </div>

            {/* B2 speed selector */}
            {level === 'B2' && (
              <div className="flex flex-col items-center mt-4" data-testid="speed-selector">
                <p className="text-[9px] text-[#6B4E37] uppercase tracking-[0.2em] font-bold mb-2">
                  Vitesse de lecture
                </p>
                <div className="flex gap-1.5 bg-white border border-[#E8E4DC] rounded-full p-1">
                  {[
                    { key: 'slow', label: 'Lent' },
                    { key: 'native', label: 'Naturel' },
                    { key: 'fast', label: 'Rapide' },
                  ].map((s) => (
                    <button
                      key={s.key}
                      onClick={() => setSpeed(s.key)}
                      className={`text-[10px] font-bold tracking-wider rounded-full px-3 py-1 transition-all ${
                        speed === s.key
                          ? 'bg-[#D4AF37] text-[#1A2F4A]'
                          : 'text-[#6B4E37] hover:text-[#1A2F4A]'
                      }`}
                      data-testid={`speed-${s.key}`}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Module grid (no scrollbar — all 5 visible at once) */}
      <section className="pb-2">
        <div className="max-w-[600px] mx-auto px-6">
          <div className="grid grid-cols-2 gap-2.5" data-testid="modules-tabs">
            {MODULES.map((m, idx) => {
              const Icon = m.icon;
              const isActive = active === m.id;
              const isLastOdd = MODULES.length % 2 === 1 && idx === MODULES.length - 1;
              return (
                <button
                  key={m.id}
                  onClick={() => setActive(m.id)}
                  className={`group relative flex items-center gap-2.5 rounded-2xl px-4 py-3 text-left transition-all ${
                    isLastOdd ? 'col-span-2' : ''
                  } ${
                    isActive
                      ? 'bg-[#1A2F4A] text-white shadow-md border border-[#D4AF37]/40'
                      : 'bg-white text-[#1A2F4A] border border-[#E8E4DC] hover:border-[#D4AF37] hover:shadow-sm'
                  }`}
                  data-testid={`tab-${m.id}`}
                >
                  <div
                    className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${
                      isActive
                        ? 'bg-[#D4AF37]/20 text-[#D4AF37]'
                        : 'bg-[#FAF7F0] text-[#1A2F4A] group-hover:text-[#D4AF37]'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                  </div>
                  <span className="text-[12px] font-bold leading-tight tracking-wide">
                    {m.name}
                  </span>
                  {isActive && (
                    <span className="absolute top-2 right-2 w-1.5 h-1.5 rounded-full bg-[#D4AF37]" />
                  )}
                </button>
              );
            })}
          </div>
          <p className="text-[11px] text-[#6B4E37] italic mt-3 px-1" data-testid="module-hint">
            {MODULES.find((m) => m.id === active)?.hint}
          </p>
        </div>
      </section>

      {/* Active module */}
      <section className="pb-20">
        <div className="max-w-[600px] mx-auto px-6 mt-4" data-testid={`module-${active}`}>
          {active === 'prononce' && <PrononceCompareModule key={`prononce-${level}`} level={level} onSave={handleSave} />}
          {active === 'defis' && <DefisModule key={`defis-${level}`} level={level} />}
          {active === 'rythme' && <RythmeModule key={`rythme-${level}`} level={level} onSave={handleSave} />}
          {active === 'bouche' && <BoucheModule key={`bouche-${level}`} level={level} />}
          {active === 'ecoute' && <EcouteModule key={`ecoute-${level}`} level={level} />}

          <HistoryPanel history={history} onClear={handleClear} />
        </div>
      </section>
    </div>
  );
}
